import { Link, useNavigate } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PlanCards } from "@/components/PlanCards";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

export const PricingSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <section id="offres" className="px-5 py-24">
      <div className="mx-auto max-w-6xl">
        <p className="eyebrow">Offres</p>
        <h2 className="mt-3 max-w-2xl text-3xl font-bold tracking-tight sm:text-4xl">Commence gratuitement, évolue quand tu veux.</h2>
        <p className="mt-4 max-w-2xl text-muted-foreground">Sans engagement. <b className="text-foreground">Offre étudiante : −20 %</b> sur Premium et Pro après vérification de ta carte étudiante. Les abonnements payants nécessitent d'être majeur ou l'accord d'un représentant légal.</p>
        <div className="mt-12">
          <PlanCards
            currentPlan={user?.plan}
            onSelect={(id) => navigate(user ? (id === "free" ? "/app" : "/app/offres") : "/connexion")}
            ctaLabel={(p) => (p.id === "free" ? "Commencer gratuitement" : `Choisir ${p.name}`)}
          />
        </div>
      </div>
    </section>
  );
};

const FAQ = [
  ["Ai-je le droit d'enregistrer mon cours ?", "Seulement avec l'autorisation de ton professeur et des personnes enregistrées, et dans le respect du règlement de ton établissement. NoteMyClass AI te demande de le confirmer avant chaque enregistrement."],
  ["L'enregistrement peut-il se faire en cachette ?", "Non. Le micro ne s'active qu'après ton action, un indicateur rouge reste affiché pendant toute la durée et l'enregistrement s'arrête si tu quittes la page."],
  ["Que deviennent mes enregistrements audio ?", "L'audio est envoyé de façon chiffrée, transcrit puis supprimé immédiatement. Seuls le texte et tes notes sont conservés, pendant la durée que tu choisis (30 jours à 1 an)."],
  ["Mes notes sont-elles visibles par d'autres ?", "Non. Tes cours sont privés et rien n'est jamais publié. Personne d'autre ne peut y accéder."],
  ["Ma voix sert-elle à entraîner une IA ?", "Non. Tes enregistrements ne sont jamais utilisés pour entraîner des modèles d'intelligence artificielle."],
  ["L'IA peut-elle se tromper ?", "Oui, c'est pourquoi les passages incertains sont marqués « Passage à vérifier » et tu peux corriger tes notes à tout moment. Les compléments qui ne viennent pas du cours sont clairement signalés."],
  ["J'ai moins de 15 ans, puis-je l'utiliser ?", "Oui, avec l'accord d'un parent ou représentant légal, que tu confirmes à la première connexion."],
];

export const Faq = () => (
  <section id="faq" className="px-5 py-24">
    <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.7fr_1.3fr]">
      <div>
        <p className="eyebrow">FAQ</p>
        <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Questions fréquentes</h2>
      </div>
      <Accordion type="single" collapsible className="card-flat px-6" data-testid="faq-accordion">
        {FAQ.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`} className={i === FAQ.length - 1 ? "border-b-0" : ""}>
            <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left text-base font-semibold hover:no-underline">{q}</AccordionTrigger>
            <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="border-t px-5 py-14">
    <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-[1.5fr_1fr_1fr_1fr]">
      <div>
        <Logo />
        <p className="mt-4 max-w-xs text-sm text-muted-foreground">Écoute ton cours, on s'occupe de tes notes.</p>
      </div>
      {[
        ["Produit", [["Fonctionnalités", "/#fonctionnalites"], ["Démonstration", "/#demo"], ["Offres", "/#offres"]]],
        ["Ressources", [["FAQ", "/#faq"], ["Se connecter", "/connexion"]]],
        ["Légal", [["Confidentialité & RGPD", "/confidentialite"], ["Conditions d'utilisation", "/confidentialite#conditions"]]],
      ].map(([t, links]) => (
        <div key={t}>
          <p className="eyebrow">{t}</p>
          <ul className="mt-4 space-y-2.5 text-sm">
            {links.map(([l, h]) => (
              <li key={l}>
                {h.startsWith("/#") ? <a href={h} className="text-muted-foreground transition-colors hover:text-foreground">{l}</a>
                  : <Link to={h} className="text-muted-foreground transition-colors hover:text-foreground">{l}</Link>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <p className="mx-auto mt-12 max-w-6xl text-xs text-muted-foreground">© {new Date().getFullYear()} NoteMyClass AI. Communications chiffrées (HTTPS). Données hébergées de façon sécurisée.</p>
  </footer>
);
